import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";

export interface AgreementInfo {
  id: number;
  slug: string;
  title: string;
  htmlContent: string;
  policyVersion: string;
  reacceptancePolicy: number;
  isActive: boolean;
}

interface AgreementStatusResult {
  needsAcceptance: boolean;
  isLoading: boolean;
  agreement: AgreementInfo | null;
  refetch: () => void;
}

export function useAgreementStatus(slug: string): AgreementStatusResult {
  const api = useApi();
  const [isLoading, setIsLoading] = useState(true);
  const [needsAcceptance, setNeedsAcceptance] = useState(true);
  const [agreement, setAgreement] = useState<AgreementInfo | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await api.messaging.get(`LegalAgreements/${slug}`);
      if (resp.ok) {
        const data = await resp.json();
        setAgreement(data?.agreement ?? null);
        setNeedsAcceptance(data?.needsAcceptance ?? true);
      } else {
        // Fail closed: if we can't check, show the modal
        setNeedsAcceptance(true);
        setAgreement(null);
      }
    } catch {
      // Fail closed: network error → show the modal
      setNeedsAcceptance(true);
      setAgreement(null);
    } finally {
      setIsLoading(false);
    }
  }, [api.messaging, slug]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { needsAcceptance, isLoading, agreement, refetch: fetch };
}
